import { Form } from "antd"
import { TextInput } from "components/common"
import { memo, useCallback, useMemo } from "react"
import { useSelector } from "react-redux"
import { t } from "i18next"
import { numberProps } from "features/leads/components/addSalesModal/utils/constants"
import { useGetDataFromImageMutation, useLazyGetAccountantsQuery } from "features/leads/api"
import PaymentProofImageUpload from "features/leads/components/addSalesModal/components/addSalesForm/components/paymentProofImageUpload"
import SuggestedAmountTags from "features/leads/components/addSalesModal/components/addSalesForm/components/paymentSection/components/SuggestedAmountTags"
import RACheckbox from "features/leads/components/addSalesModal/components/addSalesForm/components/paymentSection/components/RACheckbox"
import AccountantSection from "features/leads/components/addSalesModal/components/addSalesForm/components/paymentSection/components/AccountantSection"
import ExtraFieldsSection from "features/leads/components/addSalesModal/components/addSalesForm/components/paymentSection/components/ExtraFieldsSection"
import { usePaymentProofUploadHandler } from "features/leads/components/addSalesModal/components/addSalesForm/components/paymentSection/hooks/usePaymentProofUploadHandler"
import { usePaymentRowEffects } from "features/leads/components/addSalesModal/components/addSalesForm/components/paymentSection/hooks/usePaymentRowEffects"
import {
  buildReferenceTotalSuggestionItems,
  formatAmountSuffix,
  getPaymentSuffixMessage,
  hasPaymentProofValue,
  PAYMENTS_FORM_LIST_NAME,
} from "features/leads/components/addSalesModal/components/addSalesForm/components/paymentSection/utils/paymentUtils"
import FractionalPaymentDetailsCollapse from "../fractionalPaymentDetailsCollapse"
import FractionalPsidDepositByBlock from "../fractionalPsidDepositByBlock"
import { FRACTIONAL_PAYMENT_CHANNEL_PSID, modeMetaIsPsid } from "../fractionalPaymentUtils"

const FractionalPaymentItem = memo(
  ({
    listFieldName,
    restField,
    form,
    referenceAmount,
    projectId,
    modeOfPayments,
    paymentModesMap,
    psidPaymentModeKey,
    paymentApis,
    paymentOptions,
    paymentChannel = "standard",
  }) => {
    const { isRaFlowEnabled, ocrEnabled } = paymentOptions
    const { getBankName, getPaymentMode, validateReference } = paymentApis

    const [getDataFromImage, { isLoading }] = useGetDataFromImageMutation()
    const [getAccountant, { data: accountantData = [], isLoading: isLoadingAccountant }] = useLazyGetAccountantsQuery()

    const userPlatformKey = useSelector(state => state.app.user?.info?.AgencyConfiguration?.Platform?.platformKey)

    const fullPath = useMemo(() => [PAYMENTS_FORM_LIST_NAME, listFieldName], [listFieldName])

    const paymentsWatch = Form.useWatch(PAYMENTS_FORM_LIST_NAME, form)
    const payments = Array.isArray(paymentsWatch) ? paymentsWatch : []
    const paymentProof = Form.useWatch([...fullPath, "paymentProof"], form)
    const modeOfPayment = Form.useWatch([...fullPath, "modeOfPayment"], form)
    const depositSlip = Form.useWatch([...fullPath, "bank_deposit_slip"], form)
    const enableRA = Form.useWatch([...fullPath, "enableRA"], form)
    const lineAmount = Form.useWatch([...fullPath, "amount"], form)
    const ocrData = Form.useWatch([...fullPath, "ocrData"], form)
    const chequeBank = Form.useWatch([...fullPath, "chequeBank"], form)

    const isPsidMode = useMemo(() => {
      if (paymentChannel === FRACTIONAL_PAYMENT_CHANNEL_PSID && psidPaymentModeKey) return true
      if (modeOfPayment == null || modeOfPayment === "") return false
      if (psidPaymentModeKey != null && modeOfPayment === psidPaymentModeKey) return true
      const meta = modeOfPayments?.find(m => m.key === modeOfPayment)
      return meta ? modeMetaIsPsid(meta, modeOfPayment, psidPaymentModeKey) : false
    }, [paymentChannel, modeOfPayment, psidPaymentModeKey, modeOfPayments])

    usePaymentRowEffects({
      fullPath,
      form,
      paymentProof,
      ocrData,
      modeOfPayment,
      paymentModesMap,
      validateReference,
      getBankName,
      getPaymentMode,
      chequeBank,
    })

    const handlePaymentProofUpload = usePaymentProofUploadHandler({
      fullPath,
      form,
      ocrEnabled,
      paymentModesMap,
      getDataFromImage,
    })

    const amountSuggestionItems = useMemo(() => {
      const rowAmount = +(payments[0]?.amount || 0)
      return buildReferenceTotalSuggestionItems({
        referenceAmount,
        rowIndex: listFieldName,
        previousPaymentsTotal: 0,
        listFieldName,
        totalPaymentsAllRows: rowAmount,
      })
    }, [referenceAmount, payments, listFieldName])

    const suffixHandler = useCallback(
      lineAmountArg => {
        const message = getPaymentSuffixMessage({
          amount: lineAmountArg,
          dealPrice: referenceAmount,
          vasEnabled: false,
          leadSalesInfo: null,
          vasFees: 0,
        })
        return formatAmountSuffix(message, lineAmountArg, numberProps)
      },
      [referenceAmount]
    )

    const suffixText = useMemo(() => suffixHandler(+lineAmount || 0), [suffixHandler, lineAmount])

    const isRaFirst =
      paymentChannel !== FRACTIONAL_PAYMENT_CHANNEL_PSID && isRaFlowEnabled && !hasPaymentProofValue(paymentProof)

    return (
      <>
        {!!isRaFirst && (
          <RACheckbox {...restField} listFieldName={listFieldName} fieldName="enableRA" getAccountant={getAccountant} />
        )}

        <Form.Item
          {...(!isRaFirst ? restField : {})}
          preserve
          name={[listFieldName, "amount"]}
          label={t("Amount")}
          required
          extra={
            <SuggestedAmountTags
              suggestedAmounts={amountSuggestionItems}
              onAmountClick={item => {
                const value = item?.value != null ? item.value : null
                const path = [...fullPath, "amount"]
                form.setFieldValue(path, value)
                form.validateFields([path]).catch(() => {})
              }}
            />
          }
          rules={[
            {
              validator: (_, value) => {
                const maxAmount = referenceAmount
                if (value === undefined || value === null || value === "") {
                  return Promise.reject(new Error(t("Amount is required")))
                }
                if (Math.round(+value) > Math.round(+maxAmount)) {
                  return Promise.reject(new Error(t(`Amount cannot exceed ${maxAmount}`)))
                }

                return Promise.resolve()
              },
            },
          ]}
          validateTrigger={["onChange", "onBlur"]}
        >
          <TextInput placeholder={t("Enter amount")} suffixText={suffixText} />
        </Form.Item>

        {paymentChannel === FRACTIONAL_PAYMENT_CHANNEL_PSID && !enableRA && (
          <FractionalPsidDepositByBlock
            form={form}
            listFieldName={listFieldName}
            getAccountant={getAccountant}
            accountantData={accountantData}
            isLoadingAccountant={isLoadingAccountant}
            platformKey={userPlatformKey}
            projectId={projectId}
            isRaFlowEnabled={isRaFlowEnabled}
          />
        )}

        {!!enableRA && (
          <AccountantSection
            form={form}
            accountantData={accountantData}
            isLoadingAccountant={isLoadingAccountant}
            platformKey={userPlatformKey}
            projectId={projectId}
            listFieldName={listFieldName}
          />
        )}

        {!enableRA && paymentChannel !== FRACTIONAL_PAYMENT_CHANNEL_PSID && (
          <Form.Item
            preserve
            name={[listFieldName, "paymentProof"]}
            label={t("Payment Proof")}
            required
            rules={[
              {
                validator: (_, value) =>
                  hasPaymentProofValue(value) ? Promise.resolve() : Promise.reject(t("Payment Proof is Required")),
              },
            ]}
          >
            <PaymentProofImageUpload
              setFiles={handlePaymentProofUpload}
              paymentProofProps={{
                attachmentTypeId: 1,
                api: !!ocrEnabled,
                visionAllowedonProject: !!ocrEnabled,
                detectPaymentMode: !!ocrEnabled,
              }}
              platformKey={userPlatformKey}
              projectId={projectId}
            />
          </Form.Item>
        )}

        {paymentChannel !== FRACTIONAL_PAYMENT_CHANNEL_PSID && (
          <ExtraFieldsSection
            paymentModesMap={paymentModesMap}
            form={form}
            getAccountant={getAccountant}
            accountantData={accountantData}
            isLoadingAccountant={isLoadingAccountant}
            platformKey={userPlatformKey}
            projectId={projectId}
            ocrEnabled={ocrEnabled}
            attachmentType={modeOfPayment || paymentProof?.imageData?.paymentModeKey}
            imageData={ocrData}
            isRaEnabled={isRaFlowEnabled}
            listFieldName={listFieldName}
            hideDepositRACheckbox={isPsidMode}
            hideExtraSectionTitle={isPsidMode}
          />
        )}

        {!enableRA && paymentChannel !== FRACTIONAL_PAYMENT_CHANNEL_PSID && hasPaymentProofValue(paymentProof) && (
          <FractionalPaymentDetailsCollapse
            form={form}
            listFieldName={listFieldName}
            ocrEnabled={ocrEnabled}
            isLoading={isLoading}
            paymentProof={paymentProof}
            depositSlip={depositSlip}
            paymentModesMap={paymentModesMap}
            modeOfPayments={modeOfPayments}
            paymentApis={paymentApis}
            psidPaymentModeKey={psidPaymentModeKey}
          />
        )}
      </>
    )
  }
)

FractionalPaymentItem.displayName = "FractionalPaymentItem"

export default FractionalPaymentItem
